/**
 * Config Manager - Gestione configurazione
 */

import { DEFAULT_CONFIG, DEFAULT_ADVANCED } from '../constants/defaults.js';
import { validateConfig } from '../utils/validator.js';

/**
 * Config Manager
 *
 * Gestisce configurazione locale, globale e remota (config-src).
 */
export class ConfigManager {
  constructor(element) {
    this.element = element;
    this.remoteConfigUrl = element.getAttribute('config-src') || element.getAttribute('data-config-src');
    this.remoteConfigLoaded = false;
    this.remoteConfigPromise = null;
    this.config = this.buildBaseConfig();
    this.spreakerConfigLoaded = false;
    this.spreakerConfigPromise = null;
  }
  
  /**
   * Restituisce la configurazione corrente (sincrona).
   * @returns {Object}
   */
  getConfig() {
    return this.config;
  }
  
  /**
   * Indica se è configurata una sorgente remota.
   * @returns {boolean}
   */
  hasRemoteConfig() {
    return Boolean(this.remoteConfigUrl);
  }
  
  /**
   * Indica se è configurato un episodio Spreaker.
   * @returns {boolean}
   */
  hasSpreakerEpisode() {
    return Boolean(this.config.spreakerEpisodeId);
  }
  
  /**
   * Carica configurazione Spreaker se configurata.
   * @returns {Promise<Object>}
   */
  async loadSpreakerConfig() {
    if (!this.hasSpreakerEpisode()) {
      return this.config;
    }
    
    if (this.spreakerConfigLoaded) {
      return this.config;
    }
    
    if (!this.spreakerConfigPromise) {
      this.spreakerConfigPromise = this.fetchSpreakerConfig();
    }
    
    return this.spreakerConfigPromise;
  }
  
  /**
   * Carica e merge la configurazione remota se configurata.
   * @returns {Promise<Object>}
   */
  async loadRemoteConfig() {
    if (!this.hasRemoteConfig()) {
      return this.config;
    }
    
    if (this.remoteConfigLoaded) {
      return this.config;
    }
    
    if (!this.remoteConfigPromise) {
      this.remoteConfigPromise = this.fetchRemoteConfig();
    }
    
    return this.remoteConfigPromise;
  }
  
  /**
   * Costruisce la configurazione di base (default + inline + globale + attributi).
   * @returns {Object}
   */
  buildBaseConfig() {
    const defaultConfig = { ...DEFAULT_CONFIG, advanced: { ...DEFAULT_ADVANCED } };
    let config = { ...defaultConfig };
    
    // Config da attributo data-config
    const configAttr = this.element.getAttribute('data-config');
    if (configAttr) {
      try {
        const inlineConfig = JSON.parse(configAttr);
        config = this.mergeConfig(config, inlineConfig);
      } catch (e) {
        console.error('Errore parsing data-config:', e);
      }
    }
    
    // Config globale
    if (window.SashaConfig) {
      config = this.mergeConfig(config, window.SashaConfig);
    }
    
    // Attributi diretti
    const theme = this.element.getAttribute('theme');
    if (theme) config.theme = theme;
    
    const themesPath = this.element.getAttribute('themes-path');
    if (themesPath) config.themesPath = themesPath;
    
    // Shadow mode da attributo o config
    const shadowMode = this.element.getAttribute('shadow-mode') || config.advanced?.shadowMode;
    config.advanced = config.advanced || {};
    config.advanced.shadowMode = (shadowMode === 'closed') ? 'closed' : 'open';
    
    this.validate(config);
    return config;
  }
  
  /**
   * Recupera configurazione remota e la merge nella config corrente.
   * @returns {Promise<Object>}
   */
  async fetchRemoteConfig() {
    try {
      const response = await fetch(this.remoteConfigUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const remoteConfig = await response.json();
      this.config = this.mergeConfig(this.config, remoteConfig || {});
      this.remoteConfigLoaded = true;
      this.validate(this.config);
    } catch (error) {
      console.error('[ConfigManager] Errore caricamento config remoto:', error);
    } finally {
      this.remoteConfigPromise = null;
    }
    
    return this.config;
  }
  
  /**
   * Merge configurazioni
   * @param {Object} target - Config target
   * @param {Object} source - Config source
   * @returns {Object} Config merged
   */
  mergeConfig(target, source) {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.mergeConfig(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }
  
  /**
   * Recupera configurazione Spreaker e la merge nella config corrente.
   * @returns {Promise<Object>}
   */
  async fetchSpreakerConfig() {
    const episodeId = this.config.spreakerEpisodeId;
    
    if (!episodeId) {
      return this.config;
    }
    
    try {
      const { SpreakerProvider } = await import('../providers/SpreakerProvider.js');
      const provider = new SpreakerProvider();
      const spreakerConfig = await provider.fetchEpisode(episodeId);
      
      // Merge: Spreaker config ha priorità su defaults ma può essere sovrascritto da config inline
      // Quindi merge: defaults < Spreaker < inline config
      this.config = this.mergeConfig(this.config, spreakerConfig);
      this.spreakerConfigLoaded = true;
      this.validate(this.config);
    } catch (error) {
      console.error('[ConfigManager] Errore caricamento config Spreaker:', error);
      // In caso di errore, mantieni config esistente
    } finally {
      this.spreakerConfigPromise = null;
    }
    
    return this.config;
  }
  
  /**
   * Valida configurazione e logga eventuali warning.
   * @param {Object} config
   */
  validate(config) {
    const validation = validateConfig(config);
    if (!validation.valid) {
      console.warn('Configurazione non valida:', validation.errors);
    }
  }
}

